import {
    CampaignOutlined,
    ChatBubbleOutlineRounded,
    PollOutlined,
} from '@mui/icons-material';
import { createLucideIcon } from 'lucide-react';
import type React from 'react';
import type { GlyphKind } from './send-kind';

/**
 * One glyph per KIND of send, shared by the calendar chips and the planner's
 * sidebar rows, so a campaign, a poll, a message and a Status look the same
 * wherever they appear.
 *
 * The message / poll / campaign glyphs are the MUI outlined set the calendar
 * grid already draws. That set has no Status glyph — nothing in it is a ring
 * broken into segments, which is what WhatsApp's own Status icon is and what
 * people will recognise. Lucide (already a dependency, used across the planner)
 * has exactly that: `circle-dashed`, eight arcs on r=10 at stroke 2. Composed
 * here from lucide's own geometry rather than drawn freehand:
 *
 *   Status          circle-dashed + circle-dot-dashed's centre dot — the
 *                   dotted ring WhatsApp uses for Status
 *
 * The ring is only ever the KIND. Whether a Status posted is said by the same
 * outcome mark every other chip uses, drawn beside it — never inside it: a ring
 * tinted red with a "!" read as a failure glyph and hid what the thing was.
 *
 * Weight and size match their MUI neighbours: at 24 units a lucide stroke of 2
 * is the same 2-unit ring the outlined MUI icons draw, so at the chip's 15px
 * both come out at 1.25px. Lucide draws with `stroke="currentColor"`, so the CSS
 * colour roles (identity, --cal-bad-mark, --cal-ok-mark) reach it unchanged.
 */

type IconNode = Parameters<typeof createLucideIcon>[1];

/** lucide `circle-dashed` v0.475 — the eight segments of the ring. */
const RING: IconNode = [
    ['path', { d: 'M10.1 2.182a10 10 0 0 1 3.8 0', key: 'ring-1' }],
    ['path', { d: 'M13.9 21.818a10 10 0 0 1-3.8 0', key: 'ring-2' }],
    ['path', { d: 'M17.609 3.721a10 10 0 0 1 2.69 2.7', key: 'ring-3' }],
    ['path', { d: 'M2.182 13.9a10 10 0 0 1 0-3.8', key: 'ring-4' }],
    ['path', { d: 'M20.279 17.609a10 10 0 0 1-2.7 2.69', key: 'ring-5' }],
    ['path', { d: 'M21.818 10.1a10 10 0 0 1 0 3.8', key: 'ring-6' }],
    ['path', { d: 'M3.721 6.391a10 10 0 0 1 2.7-2.69', key: 'ring-7' }],
    ['path', { d: 'M6.391 20.279a10 10 0 0 1-2.69-2.7', key: 'ring-8' }],
];

/** The Status ring with its centre dot (lucide `circle-dot-dashed`). */
export const StatusRingIcon = createLucideIcon('StatusRing', [
    ...RING,
    ['circle', { cx: '12', cy: '12', r: '1', key: 'dot' }],
]);

export type { GlyphKind };

type GlyphComponent = React.ComponentType<{
    className?: string;
    style?: React.CSSProperties;
    role?: string;
    'aria-label'?: string;
    'aria-hidden'?: boolean | 'true' | 'false';
}>;

/** What a send IS. The calendar chip's kind slot and the sidebar row's kind
 *  marker both read this one table. */
export const KIND_GLYPH: Record<GlyphKind, GlyphComponent> = {
    message: ChatBubbleOutlineRounded,
    poll: PollOutlined,
    campaign: CampaignOutlined,
    status: StatusRingIcon,
};
